import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Info, MonitorDown } from "lucide-react";
import { Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { INSTALL_GUIDES } from "../../lib/install-guides";
import { currentPlatform, type InstallPlatform } from "../../lib/platform";
import {
  canPromptInstall,
  isStandalone,
  onInstallPromptChange,
  promptInstall,
} from "../../lib/pwa";
import { useSession } from "../../state/session";
import { Walkthrough } from "./Walkthrough";

/** Re-render when the browser offers (or withdraws) its install prompt. */
function useCanPromptInstall(): boolean {
  const [can, setCan] = useState(canPromptInstall);
  useEffect(() => onInstallPromptChange(() => setCan(canPromptInstall())), []);
  return can;
}

/**
 * The install quest's guide — only for the platform this device is on
 * (lib/install-guides.ts). Where the browser offers its own install dialog
 * (Chromium on Android and desktop), one button does it; the animated steps
 * stay below as the fallback.
 */
export function InstallGuide({
  eventId,
  platformOverride,
}: {
  eventId: string;
  platformOverride?: InstallPlatform;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const { api } = useSession();
  const platform = platformOverride ?? currentPlatform(isStandalone());
  const guide = INSTALL_GUIDES[platform];
  const canPrompt = useCanPromptInstall();
  const [accepted, setAccepted] = useState(false);

  // Opened inside the installed app: report it right here too, so a quest
  // whose window opened after this launch's report still completes.
  useEffect(() => {
    if (guide.kind !== "installed") return;
    void api.participant.reportPwaInstalled
      .mutate({ eventId })
      .then((r) => (r.bucksAwarded > 0 ? queryClient.invalidateQueries() : undefined))
      .catch(() => undefined);
  }, [guide.kind, eventId]);

  if (guide.kind === "installed") {
    return <p className="text-base text-fg">{t("ec2.install.done")}</p>;
  }
  if (guide.kind === "unsupported") {
    return (
      <p className="flex gap-2 text-base text-fg" data-testid="ec-install-unsupported">
        <Info size={18} aria-hidden className="mt-0.5 shrink-0 text-fg-secondary" />
        {t(guide.messageKey)}
      </p>
    );
  }

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") setAccepted(true);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="ec-install-guide" data-platform={platform}>
      {accepted ? (
        <p role="status" className="text-base font-medium text-accent">
          {t("ec2.install.done")} {t("ec2.install.doneHint")}
        </p>
      ) : (
        guide.promptable &&
        canPrompt && (
          <div className="flex flex-col gap-1">
            <Button
              variant="primary"
              onClick={() => void install()}
              data-testid="ec-install-prompt"
            >
              <MonitorDown size={18} aria-hidden /> {t("ec2.install.button")}
            </Button>
            <p className="text-[13px] text-fg-secondary">{t("ec2.install.buttonHint")}</p>
            <p className="mt-3 text-[13px] font-medium text-fg-secondary">
              {t("ec2.install.orManual")}
            </p>
          </div>
        )
      )}
      {guide.noteKey && (
        <p className="flex gap-2 rounded-xl bg-surface-raised p-3 text-[13px] text-fg">
          <Info size={16} aria-hidden className="mt-0.5 shrink-0 text-accent" />
          {t(guide.noteKey)}
        </p>
      )}
      <Walkthrough steps={guide.steps} platform={platform} />
    </div>
  );
}
